import React, { useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import {
  decryptPasscodeFromHelpEscrow,
  decryptPasscodeWithRecovery,
  emailsMatch,
  verifyRecoveryPassword,
} from "../utils/screenTimeRecovery.js";
import { getHelpChatReply } from "../utils/screenTimeHelpChat.js";
import { isClaudeClassificationAvailable } from "../lockbox";

/**
 * Help session: recovery email/password, AI chat, verify & reveal passcode.
 *
 * @param {{
 *   visible: boolean,
 *   onClose: () => void,
 *   settings: {
 *     screenTimePasscodeEncRecovery?: string|null,
 *     screenTimePasscodeEncEscrow?: string|null,
 *     screenTimeRecoveryEmail?: string|null,
 *     screenTimeRecoveryHash?: string|null,
 *     screenTimeRecoverySalt?: string|null,
 *     screenTimeRecoverySkipped?: boolean,
 *     screenTimeHelpEscrowSalt?: string|null,
 *     habitName?: string,
 *   },
 *   bestStreak: number,
 *   lockedPageLabel?: string,
 *   onRevealedPasscode: (pin: string) => void,
 * }} props
 */
export function ScreenTimeHelpSession({
  visible,
  onClose,
  settings,
  bestStreak,
  lockedPageLabel = "Screen Time",
  onRevealedPasscode,
}) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hi — I can help you recover your Screen Time passcode. Did you set up a recovery email, or skip that step?",
    },
  ]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryPassword, setRecoveryPassword] = useState("");
  const [verifyHabit, setVerifyHabit] = useState("");
  const [verifyStreak, setVerifyStreak] = useState("");
  const [revealedPin, setRevealedPin] = useState(null);
  const scrollRef = useRef(null);

  const recoverySkipped = !!settings.screenTimeRecoverySkipped;
  const hasRecoveryEmail = !!(settings.screenTimeRecoveryEmail && settings.screenTimeRecoveryHash);

  useEffect(() => {
    if (!visible) {
      setRevealedPin(null);
      setRecoveryEmail("");
      setRecoveryPassword("");
      setVerifyHabit("");
      setVerifyStreak("");
      setChatInput("");
      setMessages([
        {
          role: "assistant",
          text: "Hi — I can help you recover your Screen Time passcode. Did you set up a recovery email, or skip that step?",
        },
      ]);
    }
  }, [visible]);

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatLoading) return;
    const next = [...messages, { role: "user", text }];
    setMessages(next);
    setChatInput("");
    setChatLoading(true);
    try {
      const reply = await getHelpChatReply(next, {
        recoverySkipped,
        hasRecoveryEmail,
        lockedPageLabel,
      });
      setMessages((prev) => [...prev, { role: "assistant", text: reply }]);
    } finally {
      setChatLoading(false);
      setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 100);
    }
  };

  const tryEmailRecovery = async () => {
    if (!settings.screenTimePasscodeEncRecovery) {
      Alert.alert("Not available", "This passcode was set before recovery was added. Use Verify & reveal below.");
      return;
    }
    if (!settings.screenTimeRecoveryEmail || !settings.screenTimeRecoveryHash) {
      Alert.alert("Recovery not set up", "You skipped recovery. Use Verify & reveal instead.");
      return;
    }
    if (!emailsMatch(recoveryEmail, settings.screenTimeRecoveryEmail)) {
      Alert.alert("Email mismatch", "That email does not match your saved recovery email.");
      return;
    }
    const ok = await verifyRecoveryPassword(
      recoveryPassword,
      settings.screenTimeRecoverySalt,
      settings.screenTimeRecoveryHash,
    );
    if (!ok) {
      Alert.alert("Wrong recovery password", "Try again or use Verify & reveal below.");
      return;
    }
    const pin = await decryptPasscodeWithRecovery(
      settings.screenTimePasscodeEncRecovery,
      recoveryPassword,
      settings.screenTimeRecoverySalt,
    );
    if (!pin) {
      Alert.alert("Could not decrypt", "Use Verify & reveal or contact support.");
      return;
    }
    setRevealedPin(pin);
    onRevealedPasscode(pin);
  };

  const tryVerifyReveal = async () => {
    if (!settings.screenTimePasscodeEncEscrow || !settings.screenTimeHelpEscrowSalt) {
      Alert.alert("Not available", "No help escrow for this passcode. Set a new passcode in Screen Time after unlocking.");
      return;
    }
    const habitOk =
      verifyHabit.trim().toLowerCase() === (settings.habitName || "Procsystim").trim().toLowerCase();
    const streakNum = Number(verifyStreak);
    if (!habitOk || streakNum !== bestStreak) {
      Alert.alert(
        "Verification failed",
        `Enter your exact habit name (${settings.habitName || "Procsystim"}) and best streak (${bestStreak} days) from Insights.`,
      );
      return;
    }
    const pin = await decryptPasscodeFromHelpEscrow(
      settings.screenTimePasscodeEncEscrow,
      settings.screenTimeHelpEscrowSalt,
    );
    if (!pin) {
      Alert.alert("Could not reveal", "Try email recovery or set a new passcode after reinstall.");
      return;
    }
    setRevealedPin(pin);
    onRevealedPasscode(pin);
  };

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === "ios" ? "padding" : undefined}
      >
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Screen Time Help</Text>
          <Pressable onPress={onClose}>
            <Text style={styles.close}>Close</Text>
          </Pressable>
        </View>

        <ScrollView ref={scrollRef} style={styles.scroll} contentContainerStyle={styles.scrollContent}>
          <Text style={styles.intro}>
            Passcode locks {lockedPageLabel} so block lists are harder to bypass. Recovery stays on this device only.
          </Text>

          {revealedPin ? (
            <View style={styles.revealBox}>
              <Text style={styles.revealLabel}>Your passcode</Text>
              <Text style={styles.revealPin}>{revealedPin}</Text>
              <Text style={styles.revealHint}>Tap Unlock on the previous screen to use it. Do not share this.</Text>
            </View>
          ) : null}

          <Text style={styles.sectionTitle}>AI help</Text>
          <Text style={styles.sectionSub}>
            {isClaudeClassificationAvailable() ? "Claude-powered" : "Offline"} guidance
          </Text>
          <View style={styles.chatBox}>
            {messages.map((m, i) => (
              <View
                key={`m_${i}`}
                style={[styles.bubble, m.role === "user" ? styles.bubbleUser : styles.bubbleBot]}
              >
                <Text style={[styles.bubbleText, m.role === "user" ? styles.bubbleTextUser : null]}>{m.text}</Text>
              </View>
            ))}
            {chatLoading ? <ActivityIndicator style={{ marginTop: 8 }} color="#4F46E5" /> : null}
          </View>
          <View style={styles.chatInputRow}>
            <TextInput
              style={styles.chatInput}
              value={chatInput}
              onChangeText={setChatInput}
              placeholder="Ask how to recover…"
              placeholderTextColor="#9CA3AF"
              onSubmitEditing={() => void sendChat()}
            />
            <Pressable style={styles.sendBtn} onPress={() => void sendChat()}>
              <Text style={styles.sendBtnText}>Send</Text>
            </Pressable>
          </View>

          {hasRecoveryEmail && !recoverySkipped ? (
            <>
              <Text style={styles.sectionTitle}>Recover with email</Text>
              <TextInput
                style={styles.input}
                value={recoveryEmail}
                onChangeText={setRecoveryEmail}
                placeholder="Recovery email"
                autoCapitalize="none"
                keyboardType="email-address"
              />
              <TextInput
                style={styles.input}
                value={recoveryPassword}
                onChangeText={setRecoveryPassword}
                placeholder="Recovery password"
                secureTextEntry
              />
              <Pressable style={styles.primaryBtn} onPress={() => void tryEmailRecovery()}>
                <Text style={styles.primaryBtnText}>Recover passcode</Text>
              </Pressable>
            </>
          ) : null}

          <Text style={styles.sectionTitle}>Verify & reveal</Text>
          <Text style={styles.sectionSub}>
            For skipped recovery or if email recovery fails — prove it is your data (habit name + best streak from Insights).
          </Text>
          <TextInput
            style={styles.input}
            value={verifyHabit}
            onChangeText={setVerifyHabit}
            placeholder={`Habit name (e.g. ${settings.habitName || "Procsystim"})`}
          />
          <TextInput
            style={styles.input}
            value={verifyStreak}
            onChangeText={setVerifyStreak}
            placeholder={`Best streak days (e.g. ${bestStreak})`}
            keyboardType="number-pad"
          />
          <Pressable style={styles.secondaryBtn} onPress={() => void tryVerifyReveal()}>
            <Text style={styles.secondaryBtnText}>Verify & reveal passcode</Text>
          </Pressable>
        </ScrollView>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1, backgroundColor: "#fff" },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: Platform.OS === "ios" ? 52 : 24,
    paddingHorizontal: 16,
    paddingBottom: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#E5E7EB",
  },
  headerTitle: { fontSize: 18, fontWeight: "800", color: "#111" },
  close: { color: "#2563EB", fontWeight: "700" },
  scroll: { flex: 1 },
  scrollContent: { padding: 16, paddingBottom: 32 },
  intro: { fontSize: 13, color: "#6B7280", lineHeight: 19, marginBottom: 16 },
  sectionTitle: { fontSize: 15, fontWeight: "800", color: "#111", marginTop: 16, marginBottom: 4 },
  sectionSub: { fontSize: 12, color: "#6B7280", marginBottom: 10, lineHeight: 17 },
  chatBox: {
    backgroundColor: "#F8FAFC",
    borderRadius: 12,
    padding: 12,
    minHeight: 120,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  bubble: {
    alignSelf: "flex-start",
    maxWidth: "92%",
    padding: 10,
    borderRadius: 12,
    marginBottom: 8,
    backgroundColor: "#EEF2FF",
  },
  bubbleUser: { alignSelf: "flex-end", backgroundColor: "#4F46E5" },
  bubbleBot: {},
  bubbleText: { fontSize: 13, color: "#1E293B", lineHeight: 18 },
  bubbleTextUser: { color: "#fff" },
  chatInputRow: { flexDirection: "row", gap: 8, marginTop: 8, marginBottom: 8 },
  chatInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
  },
  sendBtn: {
    backgroundColor: "#4F46E5",
    paddingHorizontal: 14,
    justifyContent: "center",
    borderRadius: 10,
  },
  sendBtnText: { color: "#fff", fontWeight: "700" },
  input: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    marginBottom: 8,
    backgroundColor: "#FAFAFA",
  },
  primaryBtn: {
    backgroundColor: "#1ABC9C",
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: "center",
    marginTop: 4,
  },
  primaryBtnText: { color: "#fff", fontWeight: "800" },
  secondaryBtn: {
    backgroundColor: "#111",
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: "center",
    marginTop: 4,
  },
  secondaryBtnText: { color: "#fff", fontWeight: "800" },
  revealBox: {
    backgroundColor: "#ECFDF5",
    borderWidth: 1,
    borderColor: "#6EE7B7",
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  revealLabel: { fontSize: 12, fontWeight: "700", color: "#047857" },
  revealPin: { fontSize: 28, fontWeight: "800", color: "#065F46", marginVertical: 8, letterSpacing: 4 },
  revealHint: { fontSize: 11, color: "#059669" },
});
